import appConfig from '@/features/app/constants/app-config';
import cookieConsentConfigurations from '@/features/app/constants/cookie-consent-configurations';
import useAdminSettingsQuery from '@/features/app/queries/useAdminSettingsQuery';
import CustomTrans from '@/shared/components/custom-trans';
import {
  Accordion,
  AccordionDetails,
  AccordionHeading,
  AccordionItem,
  Button,
} from '@thisisdmg/tirecloud-patterns';
import { Modal } from '@thisisdmg/tirecloud-patterns/v2';
import { Checkbox, Divider, Switch, Tooltip } from 'antd';
import { isEqual } from 'lodash-es';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function CookieConsentModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [isAccepted, setIsAccepted] = useState(false);
  const [hasSavedConsent, setHasSavedConsent] = useState(false);
  const { data: adminSettings } = useAdminSettingsQuery();
  // const updateUser = useUpdateUserMutation();

  const [originalConsentSettings, setOriginalConsentSettings] = useState<{
    [key: string]: boolean;
  }>({});
  const [consentSettings, setConsentSettings] = useState<{
    [key: string]: boolean;
  }>({});

  const setDefaultConsentSettings = useCallback(() => {
    const cookieSettings: { [key: string]: boolean } = {};
    for (const cookieGroup of cookieConsentConfigurations) {
      for (const subCookie of cookieGroup.cookies) {
        if (subCookie.required) {
          cookieSettings[subCookie.id] = true;
        } else {
          cookieSettings[subCookie.id] = subCookie.defaultValue ?? true;
        }
      }
    }
    setConsentSettings(cookieSettings);
    setOriginalConsentSettings(cookieSettings);
  }, []);

  const loadConsentSettings = useCallback(() => {
    const savedConsentSettings = localStorage.getItem(appConfig.cookieConsentKey);
    if (savedConsentSettings) {
      try {
        const parsedSettings = JSON.parse(savedConsentSettings);
        const cookieSettings: { [key: string]: boolean } = {};

        for (const cookieGroup of cookieConsentConfigurations) {
          for (const subCookie of cookieGroup.cookies) {
            if (subCookie.required) {
              cookieSettings[subCookie.id] = true;
            } else if (typeof parsedSettings[subCookie.id] === 'boolean') {
              cookieSettings[subCookie.id] = parsedSettings[subCookie.id];
            } else {
              cookieSettings[subCookie.id] = subCookie.defaultValue ?? true;
            }
          }
        }

        setConsentSettings(cookieSettings);
        setOriginalConsentSettings(cookieSettings);
        setHasSavedConsent(true);
      } catch (e) {
        setDefaultConsentSettings();
      }
    } else {
      setDefaultConsentSettings();
    }
  }, [setDefaultConsentSettings]);

  useEffect(() => {
    loadConsentSettings();
  }, [loadConsentSettings]);

  if (!isOpen) return null;

  return (
    <Modal
      footer={null}
      closable={hasSavedConsent}
      open={isOpen}
      onCancel={hasSavedConsent ? onClose : undefined}
      zIndex={1200}
    >
      <div className="cookie-consent-modal-content">
        <h1 className="tw:text-(length:--font-size-heading-4) tw:mb-(--spacing-4)">
          {t('component.trackingConsentModal.title')}
        </h1>
        <Accordion>
          {cookieConsentConfigurations.map((cookie) => {
            return (
              <AccordionItem key={cookie.id}>
                <AccordionHeading>
                  <div>
                    <h3 className="tw:text-(length:--font-size-heading-5)">
                      {/* @ts-expect-error cannot be typed */}
                      {t(`component.trackingConsentModal.cookieDescriptions.${cookie.id}.name`)}
                    </h3>
                    {/* <p className='tw:text-(length:--font-size-small) tw:font-normal'>{cookie.description}</p> */}
                  </div>
                </AccordionHeading>
                <AccordionDetails>
                  {cookie.cookies.map((subCookie, index, array) => {
                    return (
                      <Fragment key={subCookie.id}>
                        <div>
                          <div className="tw:flex tw:items-center tw:justify-between">
                            <h4 className="tw:text-(length:--font-size-heading-6)">
                              {t(
                                // @ts-expect-error cannot be typed
                                `component.trackingConsentModal.cookieDescriptions.${cookie.id}.${subCookie.id}.name`
                              )}
                            </h4>
                            <Switch
                              className={`cookie-switch-${cookie.id}-${subCookie.id}`}
                              disabled={subCookie.required}
                              defaultChecked={
                                subCookie.required ||
                                (consentSettings[subCookie.id] ?? subCookie.defaultValue ?? true)
                              }
                              onChange={(v) => {
                                setConsentSettings((prev) => {
                                  return {
                                    ...prev,
                                    [subCookie.id]: v,
                                  };
                                });
                              }}
                            />
                          </div>
                          <p className="tw:text-(length:--font-size-small) tw:font-normal">
                            {t(
                              // @ts-expect-error cannot be typed
                              `component.trackingConsentModal.cookieDescriptions.${cookie.id}.${subCookie.id}.description`
                            )}
                          </p>
                          {subCookie.cookie_names.length > 0 && (
                            <p className="tw:text-(length:--font-size-small) tw:font-normal">
                              <span>{t('component.trackingConsentModal.cookies')}: </span>
                              <span>{subCookie.cookie_names?.join(', ')}</span>
                            </p>
                          )}
                        </div>
                        {index !== array.length - 1 && <Divider />}
                      </Fragment>
                    );
                  })}
                </AccordionDetails>
              </AccordionItem>
            );
          })}
        </Accordion>
        {!hasSavedConsent && (
          <Checkbox
            checked={isAccepted}
            onChange={(v) => {
              setIsAccepted(v.target.checked);
            }}
            className="cookie-consent-policy-accept-checkbox"
          >
            <CustomTrans
              i18nKey="component.trackingConsentModal.question"
              components={{
                linkElem: adminSettings?.privacyPolicyUrl ? (
                  <a
                    className="tw:font-bold tw:underline"
                    href={adminSettings?.privacyPolicyUrl ?? ''}
                    target="_blank"
                    rel="noreferrer"
                  />
                ) : (
                  <Link to="/privacy-policy" className="tw:font-bold tw:underline" />
                ),
              }}
            />
          </Checkbox>
        )}
        <div className="tw:mt-(--spacing-6) tw:ltr:text-right tw:rtl:text-left">
          <Tooltip
            title={
              hasSavedConsent
                ? ''
                : !isAccepted
                  ? t('component.trackingConsentModal.pleaseAcceptToContinue')
                  : ''
            }
            placement="top"
            arrow
          >
            <div className="tw:inline-block">
              <Button
                className="cookie-consent-accept-button"
                onClick={() => {
                  // updateUser.mutate({ id: user?.id ?? '', trackingConsent: true });
                  if (hasSavedConsent || isAccepted) {
                    localStorage.setItem(
                      appConfig.cookieConsentKey,
                      JSON.stringify(consentSettings)
                    );
                    loadConsentSettings();
                    onClose();
                    window.dispatchEvent(new Event('app:cookieConsentUpdated'));
                  }
                }}
                disabled={
                  hasSavedConsent ? isEqual(consentSettings, originalConsentSettings) : !isAccepted
                }
              >
                {t('component.trackingConsentModal.accept')}
              </Button>
            </div>
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
}

export default CookieConsentModal;
